import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { authHeader, clearUserToken, getUserToken } from "@/utils/userAuth";
import { trackPurchase, trackLessonMilestone, trackLead, getMetaCookies } from "@/utils/pixel";
import RequestL2InfoDialog from "@/components/RequestL2InfoDialog";
import UpsellConfirmDialog from "@/components/UpsellConfirmDialog";

import { LESSON_META, UPSELL_INFO } from "./lesson/lessonConstants";
import usePageTimeTracking from "./lesson/usePageTimeTracking";
import LessonVideoPlayer from "./lesson/LessonVideoPlayer";
import LessonBelowVideoCTAs from "./lesson/LessonBelowVideoCTAs";
import LessonEndPopup from "./lesson/LessonEndPopup";
import {
  LessonHeader, FullScreenLoading, FullScreenError, LockedLessonScreen,
} from "./lesson/LessonChrome";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LessonPage() {
  const params = useParams();
  const navigate = useNavigate();
  const n = parseInt(params.n, 10);
  const meta = LESSON_META[n];

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ended, setEnded] = useState(false);
  const [upsellLoading, setUpsellLoading] = useState(false);
  const [upsellError, setUpsellError] = useState("");
  const [showUpsellConfirm, setShowUpsellConfirm] = useState(false);
  const [showL2InfoRequest, setShowL2InfoRequest] = useState(false);
  const [l2InfoCtaVisible, setL2InfoCtaVisible] = useState(false);
  const [endingSoonCtaVisible, setEndingSoonCtaVisible] = useState(false);

  const playerRef = useRef(null);
  const startedRef = useRef(false);
  const completedRef = useRef(false);
  const endingSoonTrackedRef = useRef(false);
  const l2RequestCtaTrackedRef = useRef(false);

  // ── Auth gate ───────────────────────────────────────────────────────────
  useEffect(() => {
    const token = getUserToken();
    if (!token) {
      navigate("/?login=1");
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/users/me`, { headers: authHeader() });
        setUser(data);
      } catch {
        clearUserToken();
        navigate("/?login=1");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const denied = user && meta && !user[meta.accessFlag];

  // ── Tracking ────────────────────────────────────────────────────────────
  const fireTrack = (eventName, extra = {}) => {
    axios.post(`${API}/track`, { event_name: eventName, payload: { lesson: n, ...extra } },
      { headers: authHeader() }).catch(() => {});
    // Mirror lesson_started / lesson_completed milestones to Meta Pixel.
    const m = /^lesson_(\d+)_(started|completed)$/.exec(eventName);
    if (m && user) {
      const lessonN = m[1];
      const action = m[2] === "started" ? "Started" : "Completed";
      try { trackLessonMilestone(`Lesson${lessonN}${action}`, `${eventName}_${user.id}`, eventName); } catch { /* ignore */ }
    }
  };

  usePageTimeTracking({ user, meta, n, playerRef, fireTrack });

  // ── Vimeo player handlers ───────────────────────────────────────────────
  const handlePlay = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    if (user) {
      const key = `np_started_fired:${user.id}:${meta.trackKey}`;
      try {
        if (!localStorage.getItem(key)) {
          localStorage.setItem(key, new Date().toISOString());
          fireTrack(`${meta.trackKey}_started`);
        }
      } catch {
        fireTrack(`${meta.trackKey}_started`);
      }
    }
  };

  const handleTimeUpdate = ({ percent, seconds, duration }) => {
    // L.4: at 80% surface the "Richiedi info per sbloccare resto corso" CTA
    // below the video (previously fired on L.1 → moved here after the
    // 2026-07-02 restructure: L.1-4 are free, gate now at end of L.4).
    if (n === 4 && !l2InfoCtaVisible && percent >= 0.8) {
      setL2InfoCtaVisible(true);
      if (!l2RequestCtaTrackedRef.current) {
        l2RequestCtaTrackedRef.current = true;
        fireTrack("l4_info_cta_shown_80pct");
      }
    }
    // 20s before end → ending-soon CTA.
    if (duration > 30 && !endingSoonCtaVisible && (duration - seconds) <= 20) {
      setEndingSoonCtaVisible(true);
      if (!endingSoonTrackedRef.current) {
        endingSoonTrackedRef.current = true;
        fireTrack(`${meta.trackKey}_ending_cta_shown`);
      }
    }
  };

  const handleEnded = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    setEnded(true);
    fireTrack(`${meta.trackKey}_completed`);
  };

  // ── Upsell / next-step routing ──────────────────────────────────────────
  const handleUpsellClick = async () => {
    // ── Fast path ─────────────────────────────────────────────────────────
    // If the user already has access to the NEXT lesson, the CTA must be a
    // plain navigation — never trigger Stripe checkout / sales pages for a
    // paying customer who already unlocked downstream lessons.
    const alreadyHasNext =
      (n === 2 && (user?.has_access_l3 || user?.has_access_full_course)) ||
      (n === 3 && (user?.has_access_l4 || user?.has_access_full_course)) ||
      (n === 4 && user?.has_access_full_course);
    if (alreadyHasNext && n < 9) {
      navigate(`/corso/lezione/${n + 1}`);
      return;
    }
    // Post-2026-07-02 funnel: L.1..L.3 → plain nav to the next lesson.
    if (meta.upsellKind === "next_lesson" && meta.nextHref) {
      navigate(meta.nextHref);
      return;
    }
    // L.4 → "Richiedi informazioni per sbloccare il resto del corso".
    // Same flow that used to fire at end of L.1: opens the RequestL2InfoDialog
    // (kept named `l2_info_requested` server-side for backward compat with
    // Meta CAPI event history + existing WhatsApp R10/R11 triggers). If the
    // user has already given consent OR already requested, we short-circuit.
    if (meta.upsellKind === "request_info_rest_of_course") {
      if (user?.l2_info_requested_at) return;
      if (user?.consent_call) {
        fireTrack("l4_info_request_silent_fire");
        try {
          const { fbp, fbc } = getMetaCookies();
          const { data } = await axios.post(`${API}/leads/request-l2-info`, {
            consent_call: true, fbp, fbc,
            landing_page_url: window.location.href,
          }, { headers: authHeader() });
          setUser((u) => (u ? { ...u, l2_info_requested_at: new Date().toISOString() } : u));
          setEnded(false);
          fireTrack("l2_info_requested");
          if (data?.event_id) trackLead(data.event_id);
        } catch { /* swallow */ }
        return;
      }
      setShowL2InfoRequest(true);
      fireTrack("l4_info_request_dialog_opened");
      return;
    }
    handleUpsell();
  };

  const handleUpsell = async () => {
    setUpsellLoading(true);
    setUpsellError("");
    try {
      if (meta.isMembersOnly) {
        if (n < 9) navigate(`/corso/lezione/${n + 1}`);
        else navigate("/corso/area-membri");
        return;
      }
      // Any other upsellKind is legacy — should have been routed above.
      setUpsellError("CTA non configurato correttamente.");
    } catch (e) {
      setUpsellError(e?.response?.data?.detail || "Errore. Riprova fra poco.");
    } finally {
      setUpsellLoading(false);
    }
  };

  const handleLogout = () => { clearUserToken(); navigate("/"); };

  // ── Render ──────────────────────────────────────────────────────────────
  if (loading) return <FullScreenLoading />;
  if (!meta) return <FullScreenError msg="Lezione non trovata." />;
  if (denied) return <LockedLessonScreen n={n} />;

  return (
    <main style={{ background: "var(--np-tertiary)" }} className="min-h-screen pb-32">
      <LessonHeader user={user} onLogout={handleLogout} />

      <section className="mx-auto px-6 pt-12 md:pt-16 pb-6" style={{ maxWidth: 980 }}>
        <p className="text-[12px] tracking-[0.22em] uppercase font-semibold mb-3" style={{ color: "var(--np-secondary)" }}>
          {meta.eyebrow}
        </p>
        <h1 className="text-3xl md:text-4xl leading-tight tracking-tight" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>
          {meta.title}
        </h1>
      </section>

      <section className="mx-auto px-6" style={{ maxWidth: 980 }}>
        <LessonVideoPlayer
          n={n} title={meta.title}
          user={user} accessGranted={!!(user && user[meta.accessFlag])}
          playerRef={playerRef}
          onPlay={handlePlay} onTimeUpdate={handleTimeUpdate} onEnded={handleEnded}
        />

        <LessonBelowVideoCTAs
          n={n} user={user} meta={meta}
          l2InfoCtaVisible={l2InfoCtaVisible}
          endingSoonCtaVisible={endingSoonCtaVisible}
          upsellLoading={upsellLoading}
          fireTrack={fireTrack}
          onUpsellClick={handleUpsellClick}
        />
      </section>

      <LessonEndPopup
        open={ended}
        onOpenChange={(v) => { if (!v) setEnded(false); }}
        n={n} user={user}
        upsellLoading={upsellLoading} upsellError={upsellError}
        onCtaClick={handleUpsellClick}
      />

      <RequestL2InfoDialog
        open={showL2InfoRequest}
        onClose={() => setShowL2InfoRequest(false)}
        onSubmitted={() => {
          setUser((u) => u ? { ...u, l2_info_requested_at: new Date().toISOString(), consent_call: true } : u);
          setEnded(false);
        }}
        fireTrack={fireTrack}
        currentPhone={user?.phone}
      />

      {UPSELL_INFO[meta.upsellKind] && (
        <UpsellConfirmDialog
          open={showUpsellConfirm}
          onClose={() => { if (!upsellLoading) setShowUpsellConfirm(false); }}
          onConfirm={() => { fireTrack("upsell_confirm_pay_click", { kind: meta.upsellKind }); handleUpsell(); }}
          loading={upsellLoading}
          error={upsellError}
          productName={UPSELL_INFO[meta.upsellKind].productName}
          priceEur={UPSELL_INFO[meta.upsellKind].priceEur}
        />
      )}
    </main>
  );
}
